import { observer } from "mobx-react-lite"
import * as yup from "yup"
import styles from "../sharedStyles.module.scss"
import { useForm } from "react-hook-form"
import { yupResolver } from "@hookform/resolvers/yup"
import ArrowBackOutline from "~icons/eva/arrow-back-outline"
import { FormSelectInput, OptionsArray } from "../../../../atoms/FormSelectInput"
import {
  AgencyCruisesAnnualBookingVolume,
  AgencyGoalsAtCruisen,
  AgencyPrimaryCustomerAcquisitionMean,
  AgentCruisesOverallVolumePercent,
} from "@cruisen/contract"
import { _t } from "@cruisen/i18n"
import { FormMultiSelectInput } from "../../../../atoms/FormMultiSelectInput"
import { colors } from "@theme/colors"

type AgencySurveyForm = {
  cruisesOverallVolumePercent?: AgentCruisesOverallVolumePercent
  cruisesAnnualBookingVolume?: AgencyCruisesAnnualBookingVolume
  primaryCustomerAcquisitionMean?: AgencyPrimaryCustomerAcquisitionMean
  agencyGoalsAtCruisen?: AgencyGoalsAtCruisen[]
}

const AgencySurveyFormSchema = yup.object().shape({
  cruisesOverallVolumePercent: yup.string<AgentCruisesOverallVolumePercent>().required(),
  cruisesAnnualBookingVolume: yup.string<AgencyCruisesAnnualBookingVolume>().required(),
  primaryCustomerAcquisitionMean: yup.string<AgencyPrimaryCustomerAcquisitionMean>().required(),
  agencyGoalsAtCruisen: yup.array().optional(),
})

const cruisesOverallVolumePercentOptions: OptionsArray<AgentCruisesOverallVolumePercent> = [
  { value: "0-25", label: _t("agentProfile.cruisesOverallVolumePercent.0-25") },
  { value: "25-50", label: _t("agentProfile.cruisesOverallVolumePercent.25-50") },
  { value: "50-75", label: _t("agentProfile.cruisesOverallVolumePercent.50-75") },
  { value: "75-90", label: _t("agentProfile.cruisesOverallVolumePercent.75-90") },
  { value: "90+", label: _t("agentProfile.cruisesOverallVolumePercent.90+") },
]

const cruisesAnnualBookingVolumeOptions: OptionsArray<AgencyCruisesAnnualBookingVolume> = [
  { value: "0-50k", label: _t("agentProfile.cruisesAnnualBookingVolume.0-50k") },
  { value: "50k-100k", label: _t("agentProfile.cruisesAnnualBookingVolume.50k-100k") },
  { value: "100k-250k", label: _t("agentProfile.cruisesAnnualBookingVolume.100k-250k") },
  { value: "250k-500k", label: _t("agentProfile.cruisesAnnualBookingVolume.250k-500k") },
  { value: "500k-1m", label: _t("agentProfile.cruisesAnnualBookingVolume.500k-1m") },
  { value: "1m+", label: _t("agentProfile.cruisesAnnualBookingVolume.1m+") },
  { value: "not-disclosed", label: _t("agentProfile.cruisesAnnualBookingVolume.not-disclosed") },
]

const primaryCustomerAcquisitionMeansOptions: OptionsArray<AgencyPrimaryCustomerAcquisitionMean> = [
  { value: "social-media", label: _t("agentProfile.primaryCustomerAcquisitionMeans.social-media") },
  {
    value: "word-of-mouth",
    label: _t("agentProfile.primaryCustomerAcquisitionMeans.word-of-mouth"),
  },
  { value: "advertising", label: _t("agentProfile.primaryCustomerAcquisitionMeans.advertising") },
  {
    value: "repeat-business",
    label: _t("agentProfile.primaryCustomerAcquisitionMeans.repeat-business"),
  },
  { value: "other", label: _t("agentProfile.primaryCustomerAcquisitionMeans.other") },
]

const agencyGoalsAtCruisenOptions = [
  { value: "save-time", label: _t("agentProfile.agencyGoalsAtCruisen.save-time") },
  { value: "grow-customers", label: _t("agentProfile.agencyGoalsAtCruisen.grow-customers") },
  { value: "gain-customers", label: _t("agentProfile.agencyGoalsAtCruisen.gain-customers") },
  { value: "prevent-churn", label: _t("agentProfile.agencyGoalsAtCruisen.prevent-churn") },
]

const getAgencyGoalsDefaultValues = (agencyGoalsAtCruisen: string[]) => {
  return agencyGoalsAtCruisenOptions.reduce(
    (acc, option) => {
      if (agencyGoalsAtCruisen.includes(option.value)) {
        acc.push({
          id: option.value,
          name: option.label,
        })
      }
      return acc
    },
    [] as { id: string; name: string }[],
  )
}

export const AgencySurvey = observer(
  ({
    onSubmit,
    goBack,
    formValues,
    isUserUpsertPending,
  }: {
    onSubmit: (data: AgencySurveyForm) => void
    goBack: (data: AgencySurveyForm) => void
    formValues: AgencySurveyForm
    isUserUpsertPending: boolean
  }) => {
    const {
      control,
      handleSubmit,
      getValues,
      watch,
      formState: { isValid },
    } = useForm({
      defaultValues: {
        cruisesOverallVolumePercent: formValues.cruisesOverallVolumePercent,
        cruisesAnnualBookingVolume: formValues.cruisesAnnualBookingVolume,
        primaryCustomerAcquisitionMean: formValues.primaryCustomerAcquisitionMean,
        agencyGoalsAtCruisen: getAgencyGoalsDefaultValues(formValues.agencyGoalsAtCruisen),
      },
      resolver: yupResolver(AgencySurveyFormSchema),
    })

    const [
      cruisesOverallVolumePercent,
      cruisesAnnualBookingVolume,
      primaryCustomerAcquisitionMean,
      agencyGoalsAtCruisen,
    ] = watch([
      "cruisesOverallVolumePercent",
      "cruisesAnnualBookingVolume",
      "primaryCustomerAcquisitionMean",
      "agencyGoalsAtCruisen",
    ])
    const agencyGoalsValue =
      agencyGoalsAtCruisen.length > 0
        ? agencyGoalsAtCruisen.map((goal) => goal.name).join(", ")
        : ""

    return (
      <div className={styles.form}>
        <div className={styles.inputsContainer}>
          <div className={styles.formField}>
            <label className={styles.formLabel} htmlFor="cruisesOverallVolumePercent">
              What percentage of your booking volume is cruise?*
            </label>
            <FormSelectInput
              control={control}
              name="cruisesOverallVolumePercent"
              options={cruisesOverallVolumePercentOptions}
              label={cruisesOverallVolumePercent ? "" : "Select one"}
              testID="cruises-overall-volume-percent-select"
              parentBackgroundColor={colors.backgroundSecondary}
            />
          </div>
          <div className={styles.formField}>
            <label className={styles.formLabel} htmlFor="cruisesAnnualBookingVolume">
              My annual cruise volume is:*
            </label>
            <FormSelectInput
              control={control}
              name="cruisesAnnualBookingVolume"
              options={cruisesAnnualBookingVolumeOptions}
              label={cruisesAnnualBookingVolume ? "" : "Select one"}
              testID="cruises-annual-booking-volume-select"
              parentBackgroundColor={colors.backgroundSecondary}
            />
          </div>
          <div className={styles.formField}>
            <label className={styles.formLabel} htmlFor="primaryCustomerAcquisitionMean">
              What's your primary means of customer acquisition?*
            </label>
            <FormSelectInput
              control={control}
              name="primaryCustomerAcquisitionMean"
              options={primaryCustomerAcquisitionMeansOptions}
              label={primaryCustomerAcquisitionMean ? "" : "Select one"}
              testID="primary-customer-acquisition-mean-select"
              parentBackgroundColor={colors.backgroundSecondary}
            />
          </div>
          <div className={styles.formField}>
            <label className={styles.formLabel} htmlFor="agencyGoalsAtCruisen">
              What are your goals with Cruisen?
            </label>
            <FormMultiSelectInput
              control={control}
              name="agencyGoalsAtCruisen"
              options={agencyGoalsAtCruisenOptions}
              label={agencyGoalsValue ? "" : "Select multiple"}
              textValue={agencyGoalsValue}
              testID="agency-goals-at-cruisen-select"
              parentBackgroundColor={colors.backgroundSecondary}
            />
          </div>
        </div>
        <button
          type="button"
          className={styles.button}
          disabled={!isValid || isUserUpsertPending}
          onClick={handleSubmit((values) => {
            onSubmit({
              cruisesOverallVolumePercent: values?.cruisesOverallVolumePercent,
              cruisesAnnualBookingVolume: values?.cruisesAnnualBookingVolume,
              primaryCustomerAcquisitionMean: values?.primaryCustomerAcquisitionMean,
              agencyGoalsAtCruisen: values?.agencyGoalsAtCruisen.map((goal) => goal.id) || [],
            })
          })}
          data-testid="agency-survey-done-button"
        >
          {isUserUpsertPending ? "Loading..." : "Next"}
        </button>
        <button
          type="button"
          className={styles.previousButton}
          disabled={isUserUpsertPending}
          onClick={() => {
            const values = getValues()
            goBack({
              cruisesOverallVolumePercent: values?.cruisesOverallVolumePercent,
              cruisesAnnualBookingVolume: values?.cruisesAnnualBookingVolume,
              primaryCustomerAcquisitionMean: values?.primaryCustomerAcquisitionMean,
              agencyGoalsAtCruisen: values?.agencyGoalsAtCruisen.map((goal) => goal.id) || [],
            })
          }}
        >
          <ArrowBackOutline className={styles.previousIcon} /> Previous page
        </button>
      </div>
    )
  },
)
